import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import OpenSeadragon from 'openseadragon';
import { ArrowLeft, Loader2, Save, CheckCircle2, Pencil, Ban, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Stage, Layer, Rect, Line, Group, Text, Label, Tag } from 'react-konva';
import {
  doctorComparisonApi, comparisonApi, getErrorMessage,
  getMediaUrl, getAuthHeaders, isApiAssetUrl,
  type AnnotationPayload, type ComparisonMember, type ComparisonCluster,
} from '@/lib/api';
import { apiToViewer, viewerToApi, type ViewerAnnotationState } from '@/lib/annotationAdapter';
import { COLORS, LABEL_OPTIONS, findLabelOption } from '@/lib/labelTaxonomy';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import '../doctor/WSIViewer.css';

type ResolutionAction = 'adopt' | 'edit' | 'drop';

interface Resolution {
  action: ResolutionAction;
  label: string;
  sublabel: string;
  color: string;
}

interface LocalCluster {
  clusterId: number;
  type: ComparisonCluster['type'];
  geom: ViewerAnnotationState;
  members: ComparisonMember[];
  resolution: Resolution | null;
}

interface DrawPoint { x: number; y: number }

function centroidOf(points: DrawPoint[]): DrawPoint {
  if (!points.length) return { x: 0, y: 0 };
  return {
    x: points.reduce((s, p) => s + p.x, 0) / points.length,
    y: points.reduce((s, p) => s + p.y, 0) / points.length,
  };
}

export function ComparisonViewer() {
  const { sampleId, assignmentId, patchId } = useParams<{
    sampleId?: string;
    assignmentId?: string;
    patchId: string;
  }>();
  const navigate = useNavigate();
  const isDoctorMode = Boolean(assignmentId);
  const readOnly = !isDoctorMode;

  const [patch, setPatch] = useState<{ filename: string; image_path: string; width: number; height: number } | null>(null);
  const [clusters, setClusters] = useState<LocalCluster[]>([]);
  const [parties, setParties] = useState<{ party_id: string; party_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  // Label editing dialog (manual override of a conflict label).
  const [editClusterId, setEditClusterId] = useState<number | null>(null);
  const [labelValue, setLabelValue] = useState('NILM');
  const [sublabelValue, setSublabelValue] = useState('');
  const [selectedColor, setSelectedColor] = useState<string>(COLORS[0]);

  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  const osdContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const clusterItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [viewportBounds, setViewportBounds] = useState({ x: 0, y: 0, width: 1, height: 1 });
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });

  const conflictClusters = useMemo(
    () => clusters.filter((c) => c.type !== 'agreed'),
    [clusters],
  );

  const backPath = isDoctorMode
    ? `/doctor/assignments/${assignmentId}`
    : `/admin/samples/${sampleId}`;

  // Return to the sample page without leaving a viewer entry in history, so the
  // sample page's own back button steps up to the dashboard (not back here).
  const handleBackToSample = useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(backPath, { replace: true });
    }
  }, [navigate, backPath]);

  useEffect(() => {
    if (selectedId != null) {
      clusterItemRefs.current[selectedId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedId]);

  useEffect(() => {
    if ((!sampleId && !assignmentId) || !patchId) return;
    if (isDoctorMode) setShowAnnotations(false);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = isDoctorMode
          ? await doctorComparisonApi.patch(assignmentId!, patchId)
          : await comparisonApi.patch(sampleId!, patchId);
        if (cancelled) return;
        setPatch({
          filename: data.patch.filename,
          image_path: data.patch.image_path,
          width: data.patch.width,
          height: data.patch.height,
        });
        setParties(data.parties);

        // Pre-compute geometry/centroids for existing FINAL annotations so a
        // previously resolved patch can be re-reviewed with prior choices.
        const finals = data.final_annotations.map((a, i) => apiToViewer(a, i));
        const finalCentroids = finals.map((f) => ({ f, c: centroidOf(f.points) }));

        const local: LocalCluster[] = data.clusters.map((cluster) => {
          const geom = apiToViewer(
            {
              id: `c${cluster.cluster_id}`,
              shape_type: cluster.shape_type,
              coordinates: cluster.coordinates,
              label: cluster.agreed_label || '',
              color: cluster.color || '#94a3b8',
              attributes: {},
            },
            cluster.cluster_id,
          );

          let resolution: Resolution | null = null;
          if (cluster.type === 'agreed') {
            const rep = cluster.members.find((m) => m.present);
            resolution = {
              action: 'adopt',
              label: cluster.agreed_label || rep?.label || '',
              sublabel: rep?.sublabel || '',
              color: cluster.color || rep?.color || '#94a3b8',
            };
          } else if (data.is_resolved && finalCentroids.length) {
            // Match the cluster to the nearest saved FINAL annotation.
            const cc = centroidOf(geom.points);
            let best: { f: ViewerAnnotationState; d: number } | null = null;
            for (const { f, c } of finalCentroids) {
              const d = (c.x - cc.x) ** 2 + (c.y - cc.y) ** 2;
              if (!best || d < best.d) best = { f, d };
            }
            const tol = Math.max(geom.points.length ? 400 : 0, 400);
            if (best && best.d <= tol * tol) {
              resolution = {
                action: 'edit',
                label: best.f.label,
                sublabel: best.f.sublabel,
                color: best.f.color,
              };
            } else {
              resolution = { action: 'drop', label: '', sublabel: '', color: '' };
            }
          }

          return {
            clusterId: cluster.cluster_id,
            type: cluster.type,
            geom,
            members: cluster.members,
            resolution,
          };
        });
        setClusters(local);
      } catch (err) {
        if (!cancelled) {
          toast.error(getErrorMessage(err));
          handleBackToSample();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sampleId, assignmentId, patchId, handleBackToSample, isDoctorMode]);

  const imageUrl = patch ? getMediaUrl(patch.image_path) : '';

  useEffect(() => {
    if (!imageUrl || !osdContainerRef.current || !patch) return;
    const authHeaders = isApiAssetUrl(patch.image_path) ? getAuthHeaders() : undefined;

    const viewer = OpenSeadragon({
      element: osdContainerRef.current,
      tileSources: {
        type: 'image',
        url: imageUrl,
        buildPyramid: false,
        crossOriginPolicy: 'Anonymous',
      } as OpenSeadragon.TileSourceOptions,
      ajaxHeaders: authHeaders,
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      showNavigationControl: true,
      showNavigator: true,
      navigatorPosition: 'BOTTOM_LEFT',
      navigatorSizeRatio: 0.15,
      minZoomLevel: 0.5,
      maxZoomLevel: 40,
      visibilityRatio: 0.5,
      constrainDuringPan: false,
      animationTime: 0.3,
      crossOriginPolicy: 'Anonymous',
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true },
    });

    const updateBounds = () => {
      const b = viewer.viewport.getBounds();
      setViewportBounds({ x: b.x, y: b.y, width: b.width, height: b.height });
    };

    viewer.addHandler('open', () => {
      viewer.viewport.goHome(true);
      const tiledImage = viewer.world.getItemAt(0);
      if (tiledImage) {
        const size = tiledImage.getContentSize();
        if (size.x > 0 && size.y > 0) setImageSize({ width: size.x, height: size.y });
      }
    });

    viewer.addHandler('animation', updateBounds);
    viewer.addHandler('animation-finish', updateBounds);
    viewer.addHandler('resize', updateBounds);
    viewer.addHandler('canvas-drag', updateBounds);
    viewer.addHandler('canvas-scroll', updateBounds);

    viewerRef.current = viewer;
    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [imageUrl, patch]);

  useEffect(() => {
    const updateSize = () => {
      if (panelRef.current) {
        setStageSize({ width: panelRef.current.clientWidth, height: panelRef.current.clientHeight });
      }
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    if (panelRef.current) ro.observe(panelRef.current);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      ro.disconnect();
    };
  }, [loading]);

  const imageToScreen = useCallback((pt: DrawPoint): DrawPoint => {
    const viewer = viewerRef.current;
    if (!viewer || imageSize.width === 0) return { x: 0, y: 0 };
    const screenPt = viewer.viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(pt.x, pt.y));
    return { x: screenPt.x, y: screenPt.y };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportBounds, imageSize, stageSize]);

  const focusCluster = useCallback((cluster: LocalCluster) => {
    setSelectedId(cluster.clusterId);
    const viewer = viewerRef.current;
    if (!viewer || !cluster.geom.points.length) return;
    const c = centroidOf(cluster.geom.points);
    const vpPoint = viewer.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(c.x, c.y));
    viewer.viewport.panTo(vpPoint);
    viewer.viewport.applyConstraints();
  }, []);

  const setResolution = (clusterId: number, resolution: Resolution) => {
    setClusters((prev) => prev.map((c) => (c.clusterId === clusterId ? { ...c, resolution } : c)));
  };

  const adoptParty = (cluster: LocalCluster, member: ComparisonMember) => {
    if (readOnly) return;
    if (!member.present || !member.label) return;
    setResolution(cluster.clusterId, {
      action: 'adopt',
      label: member.label,
      sublabel: member.sublabel || '',
      color: member.color || '#94a3b8',
    });
  };

  const dropCluster = (cluster: LocalCluster) => {
    if (readOnly) return;
    setResolution(cluster.clusterId, { action: 'drop', label: '', sublabel: '', color: '' });
  };

  const openEditDialog = (cluster: LocalCluster) => {
    if (readOnly) return;
    const current = cluster.resolution;
    const matched = findLabelOption(current?.label || 'NILM');
    setEditClusterId(cluster.clusterId);
    setLabelValue(matched?.value ?? (current?.label || 'NILM'));
    setSublabelValue(current?.sublabel || matched?.sublabels[0] || '');
    setSelectedColor(current?.color || matched?.color || COLORS[0]);
  };

  const handleSaveLabel = () => {
    if (editClusterId == null) return;
    const opt = findLabelOption(labelValue);
    const canonicalLabel = opt?.value ?? labelValue.trim();
    const color = selectedColor || opt?.color || COLORS[0];
    const sublabel = sublabelValue.trim() || opt?.sublabels?.[0] || '';
    setResolution(editClusterId, { action: 'edit', label: canonicalLabel, sublabel, color });
    setEditClusterId(null);
  };

  const sublabelChoices = useMemo(() => {
    const matched = findLabelOption(labelValue);
    const priority: string[] = matched?.sublabels ? [...matched.sublabels] : [];
    const allFlat = [...new Set(LABEL_OPTIONS.flatMap((o) => [...o.sublabels]))];
    const rest = allFlat.filter((s) => !priority.includes(s)).sort((a, b) => a.localeCompare(b));
    const list = [...priority, ...rest];
    const t = sublabelValue.trim();
    if (t && !list.includes(t)) return [t, ...list];
    return list;
  }, [labelValue, sublabelValue]);

  const allResolved = conflictClusters.every((c) => c.resolution !== null);

  const handleSave = async () => {
    if ((!sampleId && !assignmentId) || !patchId) return;
    if (readOnly) return;
    if (!allResolved) {
      toast.error('Resolve every conflicting cell before saving.');
      return;
    }
    setIsSaving(true);
    try {
      const payload: AnnotationPayload[] = [];
      for (const cluster of clusters) {
        const res = cluster.resolution;
        if (!res || res.action === 'drop') continue;
        payload.push(
          viewerToApi({
            ...cluster.geom,
            label: res.label,
            sublabel: res.sublabel,
            color: res.color,
          }),
        );
      }
      if (isDoctorMode) {
        await doctorComparisonApi.resolve(assignmentId!, patchId, payload);
      }
      toast.success(isDoctorMode ? 'Patch reconciled' : 'Patch saved');
      handleBackToSample();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const renderAnnotations = () => {
    if (isDoctorMode && !showAnnotations) return null;
    return conflictClusters.map((cluster) => {
      const pts = cluster.geom.points;
      if (!pts || pts.length < 2) return null;
      const isSelected = selectedId === cluster.clusterId;
      const isHovered = hoveredId === cluster.clusterId;
      const resolved = cluster.resolution !== null && cluster.resolution.action !== 'drop';
      const dropped = cluster.resolution?.action === 'drop';

      let color = cluster.resolution?.color || cluster.geom.color || '#94a3b8';
      if (!resolved) color = '#f59e0b';
      if (dropped) color = '#64748b';

      const emphasize = isSelected || isHovered || !resolved;
      const opacity = dropped ? 0.08 : emphasize ? 0.45 : 0.3;
      const strokeWidth = isSelected ? 5 : emphasize ? 3 : 2;

      if (cluster.geom.type === 'rectangle') {
        const p1 = imageToScreen(pts[0]);
        const p2 = imageToScreen(pts[1]);
        const x = Math.min(p1.x, p2.x);
        const y = Math.min(p1.y, p2.y);
        const w = Math.abs(p2.x - p1.x);
        const h = Math.abs(p2.y - p1.y);
        return (
          <Group key={cluster.clusterId} onClick={() => setSelectedId(cluster.clusterId)}>
            <Rect
              x={x} y={y} width={w} height={h}
              fill={color} opacity={opacity}
              stroke={color} strokeWidth={strokeWidth}
              dash={!resolved ? [6, 3] : undefined}
            />
            {emphasize && (
              <Label x={x + w / 2} y={y}>
                <Tag fill="rgba(0,0,0,0.75)" cornerRadius={4} pointerDirection="down" pointerWidth={10} pointerHeight={5} />
                <Text text={resolved ? cluster.resolution!.label : 'conflict'} fill="#fff" fontSize={12} fontStyle="bold" padding={5} align="center" />
              </Label>
            )}
          </Group>
        );
      }

      const screenPoints = pts.map((p) => imageToScreen(p));
      const flat = screenPoints.flatMap((p) => [p.x, p.y]);
      const center = centroidOf(screenPoints);
      const topY = Math.min(...screenPoints.map((p) => p.y));
      return (
        <Group key={cluster.clusterId} onClick={() => setSelectedId(cluster.clusterId)}>
          <Line
            points={flat} closed
            fill={color} opacity={opacity}
            stroke={color} strokeWidth={strokeWidth}
            dash={!resolved ? [6, 3] : undefined}
          />
          {emphasize && (
            <Label x={center.x} y={topY}>
              <Tag fill="rgba(0,0,0,0.8)" cornerRadius={4} pointerDirection="down" pointerWidth={10} pointerHeight={5} />
              <Text text={resolved ? cluster.resolution!.label : 'conflict'} fill="#fff" fontSize={12} fontStyle="bold" padding={5} align="center" />
            </Label>
          )}
        </Group>
      );
    });
  };

  const toggleAnnotations = () => {
    setShowAnnotations((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedId(null);
        setHoveredId(null);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="wsi-viewer-root">
        <div className="wsi-loading">
          <div className="wsi-loading-spinner" />
          <p style={{ color: '#94a3b8', fontSize: 14 }}>Loading comparison…</p>
        </div>
      </div>
    );
  }

  if (!patch) return null;

  const resolvedCount = conflictClusters.filter((c) => c.resolution !== null).length;

  return (
    <div className="wsi-viewer-root">
      <div className="wsi-toolbar">
        <div className="wsi-toolbar-title">
          <span className="truncate max-w-[280px]">{readOnly ? 'Conflict review' : 'Reconciliation'}</span>
          <span className="text-slate-500 font-mono text-xs">{patch.filename}</span>
        </div>

        <span className="text-xs text-slate-400 px-2 select-none">
          {resolvedCount} / {conflictClusters.length} conflicts resolved
        </span>

        <div className="wsi-separator" />

        {!readOnly && (
          <>
            <Button
              variant="ghost" size="sm"
              className={`wsi-tool-btn ${showAnnotations ? 'active' : ''}`}
              onClick={toggleAnnotations}
            >
              {showAnnotations ? (
                <EyeOff className="size-4 mr-2" />
              ) : (
                <Eye className="size-4 mr-2" />
              )}
              {showAnnotations ? 'Hide conflicts' : 'Show conflicts'}
            </Button>
            <Button
              variant="ghost" size="sm"
              className="wsi-tool-btn success"
              onClick={handleSave}
              disabled={isSaving || !allResolved}
            >
              {isSaving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
              Save final
            </Button>
          </>
        )}

        {!readOnly && <div className="wsi-separator" />}

        <Button variant="ghost" size="sm" className="wsi-tool-btn" onClick={handleBackToSample}>
          <ArrowLeft className="size-4 mr-2" /> {isDoctorMode ? 'Back to assignment' : 'Back to sample'}
        </Button>
      </div>

      <div className="wsi-content">
        <div className="wsi-viewer-panel" ref={panelRef}>
          <div ref={osdContainerRef} className="wsi-osd-container" />
          <div className="wsi-konva-overlay">
            {stageSize.width > 0 && (
              <Stage width={stageSize.width} height={stageSize.height} style={{ cursor: 'grab' }}>
                <Layer>{renderAnnotations()}</Layer>
              </Stage>
            )}
          </div>
        </div>

        <div className="wsi-sidebar">
          <div className="wsi-sidebar-header">
            <span>Conflicts</span>
            <span className="wsi-sidebar-count">{conflictClusters.length}</span>
          </div>

          <div className="wsi-annotation-list">
            {conflictClusters.length === 0 ? (
              <p className="wsi-sidebar-empty">No conflicting cells on this patch.</p>
            ) : (
              conflictClusters.map((cluster, idx) => {
                const isSelected = selectedId === cluster.clusterId;
                const res = cluster.resolution;
                return (
                  <div
                    key={cluster.clusterId}
                    ref={(el) => { clusterItemRefs.current[cluster.clusterId] = el; }}
                    className={`border-b border-slate-700 p-3 cursor-pointer ${isSelected ? 'bg-slate-800/70' : ''}`}
                    onClick={() => focusCluster(cluster)}
                    onMouseEnter={() => setHoveredId(cluster.clusterId)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-200">
                        Cell #{idx + 1}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">
                          {cluster.type === 'presence' ? 'presence' : 'label'}
                        </span>
                      </span>
                      {res ? (
                        res.action === 'drop' ? (
                          <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            <Ban className="size-3" /> Dropped
                          </span>
                        ) : (
                          <span className="text-[11px] text-green-400 flex items-center gap-1">
                            <CheckCircle2 className="size-3" /> {res.label}
                          </span>
                        )
                      ) : (
                        <span className="text-[11px] text-amber-400">Unresolved</span>
                      )}
                    </div>

                    <div className="space-y-1">
                      {parties.map((party) => {
                        const member = cluster.members.find((m) => m.party_id === party.party_id);
                        const present = member?.present;
                        const chosen =
                          res?.action === 'adopt' &&
                          present &&
                          member?.label === res.label &&
                          (member?.sublabel || '') === res.sublabel;
                        return (
                          <button
                            key={party.party_id}
                            type="button"
                            disabled={!present || readOnly}
                            onClick={(e) => { e.stopPropagation(); if (member) adoptParty(cluster, member); }}
                            className={`w-full flex items-center gap-2 text-left rounded px-2 py-1 text-xs transition
                              ${present ? 'hover:bg-slate-700/60' : 'opacity-50 cursor-not-allowed'}
                              ${chosen ? 'ring-1 ring-green-500 bg-green-500/10' : ''}`}
                          >
                            <span
                              className="inline-block size-3 rounded-sm shrink-0"
                              style={{ background: member?.color || '#475569' }}
                            />
                            <span className="font-medium text-slate-300 w-24 truncate">{party.party_name}</span>
                            {present ? (
                              <span className="text-slate-200 truncate">
                                {member?.label}
                                {member?.sublabel ? ` · ${member.sublabel}` : ''}
                              </span>
                            ) : (
                              <span className="text-slate-500 italic">absent</span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {!readOnly && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm" variant="outline" className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); openEditDialog(cluster); }}
                      >
                        <Pencil className="size-3 mr-1" /> Edit
                      </Button>
                      {cluster.type === 'presence' && (
                        <Button
                          size="sm" variant="ghost" className="h-7 text-xs text-slate-400"
                          onClick={(e) => { e.stopPropagation(); dropCluster(cluster); }}
                        >
                          <Ban className="size-3 mr-1" /> Drop
                        </Button>
                      )}
                    </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {editClusterId != null && (
        <div className="wsi-label-overlay" onClick={() => setEditClusterId(null)}>
          <div className="wsi-label-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Set final label</h3>

            <div className="wsi-label-field">
              <label>Cell Classification (Label)</label>
              <Select
                value={(findLabelOption(labelValue)?.value ?? labelValue?.trim()) || 'NILM'}
                onValueChange={(v) => {
                  setLabelValue(v);
                  const opt = findLabelOption(v);
                  if (opt) {
                    setSublabelValue(opt.sublabels[0] || '');
                    if (opt.color) setSelectedColor(opt.color);
                  }
                }}
              >
                <SelectTrigger className="w-full h-10 text-sm bg-slate-900 border border-slate-500 text-slate-100">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent className="z-[1100] bg-slate-900 border-slate-600 text-slate-100">
                  {LABEL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="wsi-label-field">
              <label>Sub-Classification (Sublabel)</label>
              <Select
                value={sublabelChoices.includes(sublabelValue.trim()) ? sublabelValue.trim() : (sublabelChoices[0] || '')}
                onValueChange={setSublabelValue}
              >
                <SelectTrigger className="w-full h-10 text-sm bg-slate-900 border border-slate-500 text-slate-100">
                  <SelectValue placeholder="Select sub-classification" />
                </SelectTrigger>
                <SelectContent className="z-[1100] bg-slate-900 border-slate-600 text-slate-100 max-h-[min(60vh,320px)] overflow-y-auto">
                  {sublabelChoices.map((sub) => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="wsi-label-field">
              <label>Annotation Color</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    style={{
                      width: 24, height: 24, borderRadius: 4, background: c, cursor: 'pointer',
                      border: selectedColor === c ? '2px solid white' : '2px solid transparent',
                      boxShadow: selectedColor === c ? '0 0 0 2px #3b82f6' : 'none',
                    }}
                    title={LABEL_OPTIONS.find((l) => l.color === c)?.label || c}
                  />
                ))}
              </div>
            </div>

            <div className="wsi-label-actions">
              <button type="button" className="cancel-btn" onClick={() => setEditClusterId(null)}>Cancel</button>
              <button type="button" className="save-btn" onClick={handleSaveLabel}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
